import React, { useEffect, useState } from 'react';
import { Container, Col, Button, Input } from 'reactstrap';
import CommonTableNew from '../../Tables/ReactstrapTables/BasicTables/Common/CommonTableNew';
import { getData } from '../../../CommonElements/Functions/PostGetFun';
import { API } from '../../../api/API';
import { Image } from '../../../AbstractElements';
import { dynamicImage } from '../../../Utils';

const Inputss: React.FC<{ employeeIdProp?: number }> = ({ employeeIdProp }) => {
  const [sgpiData, setSgpiData] = useState<any[]>([]); // State to store SGPI balance data
  const [filteredData, setFilteredData] = useState<any[]>([]); // State for filtered data
  const [outletTypes, setOutletTypes] = useState<any[]>([]); // State to store outlet types
  const [loading, setLoading] = useState<boolean>(true); // State to manage loading
  const [searchTerm, setSearchTerm] = useState<string>(''); // State for search input

  // Function to fetch SGPI balance data
  const fetchSGPIBalance = async () => {
    try {
      setLoading(true);
      const serverUrl = localStorage.getItem('serverUrl');
      const url = `${serverUrl}${API.getSGPIBalance}`;
      const { data, status } = await getData(url, true, { employee_id: employeeIdProp }); // Call API
      if (status === 200 && data?.data) {
        const balanceData = data.data.balance || []; // Extract the balance array
        const enrichedData = balanceData.map((item: any) => {
          const outletType = outletTypes.find((type: any) => type.OutlettypeId === item.OutlettypeId);
          return {
            ...item,
            OutlettypeName: outletType ? outletType.OutlettypeName : 'N/A', // Match OutlettypeId and add OutlettypeName
          };
        });
        setSgpiData(enrichedData); // Set enriched data to state
        setFilteredData(enrichedData); // Initialize filtered data
        console.log('SGPI Balance Data:', enrichedData); // Print enriched data to console
      } else {
        console.error('Failed to fetch SGPI balance data');
      }
    } catch (error) {
      console.error('Error fetching SGPI balance data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Function to fetch outlet types
  const fetchOutletTypes = async () => {
    try {
      const serverUrl = localStorage.getItem('serverUrl');
      const url = `${serverUrl}${API.getOutletTypes}`;
      const { data, status } = await getData(url, true); // Call API with no params
      if (status === 200 && data?.data) {
        setOutletTypes(data.data); // Set outlet types data to state
        console.log('Outlet Types Data:', data.data); // Print outlet types data to console
      } else {
        console.error('Failed to fetch outlet types');
      }
    } catch (error) {
      console.error('Error fetching outlet types:', error);
    }
  };

  // useEffect to call fetchSGPIBalance and fetchOutletTypes on component mount
  useEffect(() => {
    fetchOutletTypes(); // Fetch outlet types first
  }, []);

  useEffect(() => {
    if (outletTypes.length > 0) {
      fetchSGPIBalance(); // Fetch SGPI balance after outlet types are loaded
    }
  }, [outletTypes]);

  // Function to calculate status
  const getStatus = (item: any) => {
    const start = new Date(item.UseStartDate);
    const end = new Date(item.UseEndDate);
    const givenDate = new Date();
    if (givenDate >= start && givenDate <= end) {
      return 'Active';
    } else {
      return 'Previous';
    }
  };

  // Function to open URL in a new tab
  const openInNewTab = (sgpiId: number, employeeId: number) => {
    const serverUrl = localStorage.getItem('serverUrl');
    const appToken = localStorage.getItem('appToken');
    const url = `${serverUrl}report/transactionReport?apptoken=${appToken}&sgpi_id=${sgpiId}&employee_id=${employeeId}`;
    window.open(url, '_blank'); // Open the URL in a new browser tab
  };

  // Function to handle search
  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toLowerCase();
    setSearchTerm(value);
    const filtered = sgpiData.filter((item: any) =>
      item.SgpiName?.toLowerCase().includes(value) ||
      item.SgpiType?.toLowerCase().includes(value) ||
      item.OutlettypeName?.toLowerCase().includes(value)
    );
    setFilteredData(filtered);
  };

  // Table headers
  const headData = [
    { id: '1', head: 'SGPI Name', key: 'SgpiName', sortKey: 'SgpiName' },
    { id: '2', head: 'SGPI Type', key: 'SgpiType', sortKey: 'SgpiType' },
    { id: '3', head: 'Brand Name', key: 'BrandName', sortKey: 'BrandName' },
    { id: '4', head: 'Balance', key: 'Balance', sortKey: 'Balance' },
    { id: '5', head: 'Start Date', key: 'UseStartDate', sortKey: 'UseStartDate' },
    { id: '6', head: 'End Date', key: 'UseEndDate', sortKey: 'UseEndDate' },
    { id: '7', head: 'Status', key: 'Status', sortKey: '' }, // Status column
    { id: '8', head: 'Is Strategic', key: 'IsStrategic', sortKey: '' }, // IsStrategic column
    { id: '9', head: 'Outlet Type', key: 'OutlettypeName', sortKey: '' }, // Outlet Type column
    { id: '10', head: 'Actions', key: 'Actions', sortKey: '' }, // Actions column
  ];

  return (
    <Container fluid>
      <Col xxl={12}>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h3>SGPI Balance</h3>
          <Input
            type="text"
            placeholder="Search..."
            value={searchTerm}
            onChange={handleSearch}
            style={{ maxWidth: '300px' }}
          />
        </div>
        {loading ? (
          <div className="text-center py-3">
            <div>
              <Image
                className="b-r-10 img-80"
                src={dynamicImage('dashboard/loader.gif')}
                alt="Loading..."
              />
            </div>
          </div>
        ) : (
          <CommonTableNew
            caption=""
            headData={headData}
            data={filteredData}
            renderRow={(row: any, rowIndex: number) => (
              <tr key={rowIndex}>
                <td>{row?.SgpiName || 'N/A'}</td>
                <td>{row?.SgpiType || 'N/A'}</td>
                <td>{row?.BrandName || 'N/A'}</td>
                <td>{row?.Balance || '0'}</td>
                <td>{row?.UseStartDate ? new Date(row.UseStartDate).toLocaleDateString() : 'N/A'}</td>
                <td>{row?.UseEndDate ? new Date(row.UseEndDate).toLocaleDateString() : 'N/A'}</td>
                <td>
                  {getStatus(row) === 'Active' ? (
                    <span style={{ color: 'green' }}>Active</span>
                  ) : (
                    <span style={{ color: 'red' }}>Previous</span>
                  )}
                </td>
                <td>
                  {row?.IsStrategic?.toString() === 'true' ? (
                    <span style={{ color: 'blue' }}>Yes</span>
                  ) : (
                    <span style={{ color: 'black' }}>No</span>
                  )}
                </td>
                <td>{row?.OutlettypeName || 'N/A'}</td>
                <td>
                  <Button
                    color="primary"
                    size="sm"
                    onClick={() => openInNewTab(row.SgpiId, row.EmployeeId)}
                  >
                    View
                  </Button>
                </td>
              </tr>
            )}
          />
        )}
      </Col>
    </Container>
  );
};

export default Inputss;